// src/components/promotion/PromotionDetailDialog.tsx
import { Modal, Typography, Row, Col, Divider, Tag, Table, Spin, Empty } from "antd";
import type { ColumnsType } from "antd/es/table";
import CustomButton from "src/components/common/CustomButton";
import type { PromotionDetail } from "src/api/promotion/promotionApi";

type Props = {
  open: boolean;
  detail: PromotionDetail | null;
  loading?: boolean;
  onClose: () => void;
};

const PromotionTypeLabel: Record<string, string> = {
  EVENT: "이벤트",
  TIME_DEAL: "타임딜",
  FLASH_SALE: "플래시세일",
  SEASONAL: "시즌특가",
};

const PromotionStatusLabel: Record<string, string> = {
  DRAFT: "초안",
  ACTIVE: "진행중",
  CANCELED: "취소됨",
};

export default function PromotionDetailDialog({ open, detail, loading, onClose }: Props) {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(
      2,
      "0"
    )}`;
  };

  const productColumns: ColumnsType<PromotionDetail["promotionProducts"][number]> = [
    { title: "상품ID", dataIndex: "productId", key: "productId" },
    {
      title: "상품코드",
      dataIndex: "productCode",
      key: "productCode",
      render: (value: string) => (
        <Typography.Text style={{ fontFamily: "monospace", fontSize: 12 }}>{value}</Typography.Text>
      ),
    },
    { title: "상품명", dataIndex: "productName", key: "productName" },
    { title: "변형ID", dataIndex: "variantId", key: "variantId", render: (v: number | null) => v || "-" },
    { title: "변형명", dataIndex: "variantName", key: "variantName", render: (v: string | null) => v || "-" },
    {
      title: "정상가",
      dataIndex: "consumerPrice",
      key: "consumerPrice",
      align: "right",
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: "프로모션가",
      dataIndex: "promotionPrice",
      key: "promotionPrice",
      align: "right",
      render: (v: number) => (
        <Typography.Text style={{ fontWeight: 600, color: "#d32f2f" }}>{v.toLocaleString()}</Typography.Text>
      ),
    },
    {
      title: "할인율",
      dataIndex: "discountRate",
      key: "discountRate",
      align: "right",
      render: (v: number) => `${v}%`,
    },
    {
      title: "판매/최대",
      key: "soldMax",
      align: "right",
      render: (_, record) => `${record.soldQuantity} / ${record.maxQuantityPerProduct}`,
    },
    {
      title: "주문당최대",
      dataIndex: "maxQuantityPerOrder",
      key: "maxQuantityPerOrder",
      align: "center",
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1000}
      title={<Typography.Text strong>프로모션 상세</Typography.Text>}
      footer={[
        <CustomButton key="close" variant="outlined" onClick={onClose}>
          닫기
        </CustomButton>,
      ]}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
          <Spin />
        </div>
      ) : detail ? (
        <>
          <Row gutter={[16, 12]}>
            <Col span={12}>
              <Typography.Text type="secondary">프로모션 ID</Typography.Text>
              <div>{detail.promotionId}</div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">프로모션 코드</Typography.Text>
              <div style={{ fontFamily: "monospace" }}>{detail.promotionCode}</div>
            </Col>
            <Col span={24}>
              <Typography.Text type="secondary">프로모션명</Typography.Text>
              <div style={{ fontWeight: 600 }}>{detail.promotionName}</div>
            </Col>
            <Col span={24}>
              <Typography.Text type="secondary">설명</Typography.Text>
              <div style={{ whiteSpace: "pre-wrap" }}>{detail.promotionDescription || "-"}</div>
            </Col>
            <Col span={8}>
              <Typography.Text type="secondary">프로모션 타입</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Tag>{PromotionTypeLabel[detail.promotionType]}</Tag>
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text type="secondary">상태</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Tag>{PromotionStatusLabel[detail.promotionStatus]}</Tag>
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text type="secondary">유효상태</Typography.Text>
              <div>{detail.effectiveStatus}</div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">시작일시</Typography.Text>
              <div>{formatDate(detail.startDate)}</div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">종료일시</Typography.Text>
              <div>{formatDate(detail.endDate)}</div>
            </Col>
            <Col span={8}>
              <Typography.Text type="secondary">활성화</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Tag color={detail.isActive ? "green" : "default"}>
                  {detail.isActive ? "ON" : "OFF"}
                </Tag>
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text type="secondary">진열순서</Typography.Text>
              <div>{detail.displayOrder}</div>
            </Col>
            <Col span={8}>
              <Typography.Text type="secondary">최대 판매 수량</Typography.Text>
              <div>
                {detail.soldQuantity} / {detail.maxQuantity}
              </div>
            </Col>
            {detail.bannerImageUrl ? (
              <Col span={24}>
                <Typography.Text type="secondary">배너 이미지</Typography.Text>
                <div style={{ marginTop: 8 }}>
                  <img
                    src={detail.bannerImageUrl}
                    alt="배너"
                    style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain" }}
                  />
                </div>
              </Col>
            ) : null}
          </Row>

          <Divider style={{ margin: "16px 0" }} />
          <Typography.Text strong>프로모션 상품 ({detail.promotionProducts.length}개)</Typography.Text>
          <div style={{ marginTop: 8, border: "1px solid #e0e0e0", borderRadius: 8 }}>
            <Table
              columns={productColumns}
              dataSource={detail.promotionProducts}
              pagination={false}
              rowKey="promotionProductId"
              scroll={{ x: 1000 }}
              locale={{ emptyText: <Empty description="상품이 없습니다." /> }}
            />
          </div>

          <Row gutter={[16, 12]} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Typography.Text type="secondary">생성일시</Typography.Text>
              <div>{formatDate(detail.createdAt)}</div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">수정일시</Typography.Text>
              <div>{formatDate(detail.updatedAt)}</div>
            </Col>
          </Row>
        </>
      ) : (
        <div style={{ padding: "24px 0", textAlign: "center" }}>
          <Typography.Text type="secondary">데이터를 불러올 수 없습니다.</Typography.Text>
        </div>
      )}
    </Modal>
  );
}
